//
// Return an Image or Video player from Airtable field
//
import React from 'react';
import {Image, View} from 'react-native';
import YoutubePlayer from 'react-native-youtube-iframe';
import InViewPort from '@coffeebeanslabs/react-native-inviewport';

import {
  AirtableAttachmentDetail,
  AirtableFeedItem,
} from '../../models/airtable';

import {ArticleVideoType, GraphicContentType} from './constants';
import styles from './styles';

export const getFeedDiscoverTileImage = (
  detail: AirtableAttachmentDetail | undefined,
  _: boolean,
  visibleCallback?: (isVisible: boolean) => void,
) => {
  if (!detail) return null;

  if (detail.type?.indexOf('image') === 0) {
    return (
      <View style={styles.tileImageContainer}>
        <Image
          source={{uri: detail.url}}
          style={[
            styles.tileImage,
            // @ts-ignore 'detail.width' is possibly 'undefined'.
            {aspectRatio: detail.width / detail.height},
          ]}
          resizeMode="cover"
        />
      </View>
    );
  } else if (detail.type === 'video') {
    const innerComponent = () => (
      <video muted style={styles.tileVideo} autoPlay loop playsInline>
        <source src={detail.url} />
      </video>
    );
    // Using InViewPort with multiple videos on the screen will overwhelm
    // the UIManager with callbacks and lock up devices. Easier to just let them
    // all play
    return visibleCallback ? (
      <InViewPort onChange={visibleCallback}>{innerComponent()}</InViewPort>
    ) : (
      innerComponent()
    );
  }

  return null;
};

export const getFeedDiscoverDetailGraphics = (
  detail: AirtableAttachmentDetail | undefined,
  vidType: string | null,
  margin: any,
  source: AirtableFeedItem | null,
  setHeadlineVideoPaused = (_: boolean) => null,
  headlineVideoPaused = false,
  articleVideoPaused = true,
  youtubeVideoPaused = true,
) => {
  if (!detail) return null;

  if (detail.type.indexOf(GraphicContentType.Image) > -1) {
    return (
      <View
        style={
          !margin
            ? styles.imageContainer
            : [styles.imageContainer, styles.topMargin]
        }>
        <Image
          source={{uri: detail.url}}
          style={[
            styles.imageResponsive,
            // @ts-ignore 'detail.width' is possibly 'undefined'.
            {aspectRatio: detail.width / detail.height},
          ]}
          resizeMode="cover"
        />
      </View>
    );
  } else if (detail.type === GraphicContentType.Video) {
    let setter = (_?: boolean) => null;
    let getter = true;

    if (vidType === ArticleVideoType.HeadlineVideo) {
      setter = headlineImageVisible =>
        setHeadlineVideoPaused(!headlineImageVisible);
      getter = headlineVideoPaused;
    } else if (vidType === ArticleVideoType.ArticleVideo) {
      setter = () => null;
      getter = articleVideoPaused;
    } else if (vidType === ArticleVideoType.YoutubeVideo) {
      setter = () => null;
      getter = youtubeVideoPaused;
    }

    if (vidType !== ArticleVideoType.YoutubeVideo) {
      return (
        <InViewPort onChange={(isVisible: boolean) => setter(isVisible)}>
          <View
            style={
              !margin
                ? styles.videoContainer
                : [styles.videoContainer, styles.topMargin]
            }>
            <video
              controls
              autoPlay={getter}
              muted
              style={styles.video}
              loop={vidType === ArticleVideoType.HeadlineVideo}>
              <source src={detail.url} />
            </video>
          </View>
        </InViewPort>
      );
    } else {
      const youtubeId = source?.youtube
        ?.replace('https://youtu.be/', '')
        .replace('https://www.youtube.com/watch?v=', '');
      return (
        <InViewPort onChange={(isVisible: boolean) => setter(isVisible)}>
          <View
            style={
              !margin
                ? styles.videoContainer
                : [styles.videoContainer, styles.topMargin]
            }>
            {/* @ts-ignore Property 'height' is required in type 'YoutubeIframeProps' */}
            <YoutubePlayer
              play={!getter}
              videoId={youtubeId}
              webViewStyle={styles.youtube}
            />
          </View>
        </InViewPort>
      );
    }
  }

  return null;
};
